import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { fmtDate } from "../lib/format";
import { printTable } from "../lib/printView";
import type { Project, Deliverable } from "../lib/types";
import { RecordBadge } from "../components/RecordStatus";

/* A formal, e-signed approval of a set of drawings at a stage — distinct from
   the engagement (which appoints us) and from a review comment (which is
   feedback). This is the client putting their name to "build from these". */

export function IssueApprovalModal({ p, drawings, by, onClose }: { p: Project; drawings: Deliverable[]; by: string; onClose: () => void }) {
  const phases = [...new Set(drawings.map((d) => d.phase))];
  const [title, setTitle] = useState("");
  const [stage, setStage] = useState<string>(phases[0] ?? "IFT");
  const [note, setNote] = useState("");
  const [scope, setScope] = useState<"phase" | "all">("phase");
  const sheets = (scope === "all" ? drawings : drawings.filter((d) => d.phase === stage)).filter((d) => d.previewUrl || d.fileUrl);

  return (
    <Modal open onClose={onClose} title="Issue drawings for approval" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          Ask the client to formally sign off these drawings. They review and e-sign in their portal, and the signed record is kept against the project.
        </p>
        <div className="field"><label>Which sheets</label>
          <div className="row wrap" style={{ gap: 6, marginBottom: 8 }}>
            <button className={"chip" + (scope === "phase" ? " on" : "")} onClick={() => setScope("phase")}>By stage</button>
            <button className={"chip" + (scope === "all" ? " on" : "")} onClick={() => setScope("all")}>Everything drawn</button>
          </div>
          {scope === "phase" && (
            <select className="select" value={stage} onChange={(e) => setStage(e.target.value)}>
              {phases.map((ph) => <option key={ph} value={ph}>{ph}</option>)}
            </select>
          )}
          <p className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{sheets.length} sheet{sheets.length === 1 ? "" : "s"} with a drawing on file{sheets.length === 0 ? " — nothing to approve" : ""}</p>
        </div>
        <div className="field"><label>Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${stage} — drawing approval`} /></div>
        <div className="field"><label>Note to the client <span className="dim">(optional)</span></label><textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What signing this confirms, and what happens next." /></div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!sheets.length} onClick={() => {
            actions.issueApproval(p.id, {
              title: title.trim() || `${stage} — drawing approval`, stage, note,
              sheets: sheets.map((d) => ({ code: d.code, title: d.title, revision: d.revision })), by,
            });
            onClose();
          }}><Icon.send width={15} />Issue {sheets.length} sheet{sheets.length === 1 ? "" : "s"} for approval</button>
        </div>
      </div>
    </Modal>
  );
}

/* The record of every approval on the project, studio-side. */
export function ApprovalsPanel({ p }: { p: Project }) {
  const list = [...(p.approvals ?? [])].sort((a, b) => +new Date(b.issuedOn) - +new Date(a.issuedOn));
  const [openId, setOpenId] = useState<string | null>(null);
  if (!list.length) return null;
  return (
    <div className="card reveal" style={{ overflow: "hidden", marginTop: 22 }}>
      <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
        <div className="row" style={{ gap: 8 }}><Icon.sign width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Drawing approvals</span></div>
        <span className="dim" style={{ fontSize: 12 }}>{list.filter((a) => a.status === "signed").length} of {list.length} signed</span>
      </div>
      {list.map((a, i) => (
        <div key={a.id} style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
          <div className="row between wrap" style={{ gap: 10 }}>
            <span className="row" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
              <span className="badge accent mono" style={{ fontSize: 10.5 }}>{a.ref}</span>
              <span style={{ fontSize: 14.5, fontWeight: 600 }}>{a.title}</span>
            </span>
            <span className="badge" style={{ fontSize: 9, color: a.status === "signed" ? "var(--ok)" : a.status === "declined" ? "var(--danger)" : "var(--warn)" }}>
              {a.status === "signed" ? "Signed" : a.status === "declined" ? "Declined" : "Awaiting signature"}
            </span>
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
            {a.sheets.length} sheet{a.sheets.length === 1 ? "" : "s"} · issued {fmtDate(a.issuedOn)} by {a.issuedBy}
            {a.signedBy ? ` · ${a.status === "signed" ? "signed" : "declined"} by ${a.signedBy}${a.signedOn ? ` ${fmtDate(a.signedOn)}` : ""}` : ""}
          </div>
          {a.declineNote && <div style={{ fontSize: 12.5, marginTop: 4, color: "var(--danger)" }}>{a.declineNote}</div>}
          <div style={{ marginTop: 8 }}>
            <RecordBadge p={p} kind={a.status === "signed" ? "approval-signed" : a.status === "declined" ? "approval-declined" : "approval-issued"} refId={a.id} title={`${a.ref} — ${a.title}`} by={a.signedBy ?? a.issuedBy} compact />
          </div>
          {a.signatureDataUri && <img src={a.signatureDataUri} alt="signature" style={{ height: 44, marginTop: 8, background: "#fff", borderRadius: 6, border: "1px solid var(--line)", padding: 4 }} />}
          <div className="row wrap" style={{ gap: 8, marginTop: 9 }}>
            <button className="chip" style={{ fontSize: 11 }} onClick={() => setOpenId(openId === a.id ? null : a.id)}>{openId === a.id ? "Hide sheets" : `Show ${a.sheets.length} sheets`}</button>
            <button className="chip" style={{ fontSize: 11 }} onClick={() => printTable({
              title: `Drawing approval — ${a.ref}`, subtitle: `${p.name} · ${p.code}`,
              meta: [a.title, `Issued ${fmtDate(a.issuedOn)} by ${a.issuedBy}`, a.status === "signed" ? `Signed by ${a.signedBy} on ${fmtDate(a.signedOn!)}` : a.status === "declined" ? `Declined by ${a.signedBy}` : "Awaiting signature"],
              columns: [{ key: "code", label: "Sheet", width: "80px" }, { key: "title", label: "Title" }, { key: "revision", label: "Rev", width: "50px", align: "center" }],
              rows: a.sheets.map((s) => ({ code: s.code, title: s.title, revision: s.revision ?? "—" })),
              note: a.note || "The sheets listed above were issued for the client's formal approval.",
            })}>Print record</button>
            <span style={{ flex: 1 }} />
            <button className="iconbtn" style={{ width: 28, height: 28 }} title="Remove" onClick={() => { if (confirm(`Remove ${a.ref}?`)) actions.removeApproval(p.id, a.id); }}><Icon.close width={12} /></button>
          </div>
          {openId === a.id && (
            <div className="col" style={{ gap: 2, marginTop: 10, maxHeight: 220, overflowY: "auto" }}>
              {a.sheets.map((s, j) => (
                <div key={j} className="row between" style={{ padding: "5px 8px", borderRadius: 6, background: "var(--paper-2)", gap: 10 }}>
                  <span className="row" style={{ gap: 8, minWidth: 0 }}><span className="mono dim" style={{ fontSize: 11 }}>{s.code}</span><span style={{ fontSize: 12.5 }}>{s.title}</span></span>
                  <span className="mono dim" style={{ fontSize: 11 }}>{s.revision ?? "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
